import type {
  FriendSharedParty,
  FriendSummary,
  RemovedFriendSummary,
} from "./types/parties-api";
import { isPlaceholderUser } from "./validation";

export const FRIEND_ACTION_CODES = [
  "notOwner",
  "notFriend",
  "alreadyMember",
  "alreadyInvited",
  "self",
  "notInvitee",
  "notPending",
  "inviteNotFound",
] as const;

export type FriendActionCode = (typeof FRIEND_ACTION_CODES)[number];

export function isFriendActionCode(value: string): value is FriendActionCode {
  return (FRIEND_ACTION_CODES as readonly string[]).includes(value);
}

export type FriendshipMember = {
  userId: string;
  clerkId: string;
  joinedAt: Date | string;
};

export type FriendshipPair = {
  userId: string;
  friendUserId: string;
  sharedAt: string;
};

function memberJoinedAt(value: Date | string): number | null {
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

export function friendshipPairs(
  members: readonly FriendshipMember[],
): FriendshipPair[] {
  const realMembers = new Map<string, number>();
  for (const member of members) {
    if (!member.userId || isPlaceholderUser(member.clerkId)) {
      continue;
    }
    const joinedAt = memberJoinedAt(member.joinedAt);
    if (joinedAt === null) {
      continue;
    }
    const existing = realMembers.get(member.userId);
    if (existing === undefined || joinedAt < existing) {
      realMembers.set(member.userId, joinedAt);
    }
  }

  const ordered = [...realMembers.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  const pairs: FriendshipPair[] = [];
  for (const [userId, userJoinedAt] of ordered) {
    for (const [friendUserId, friendJoinedAt] of ordered) {
      if (userId === friendUserId) {
        continue;
      }
      pairs.push({
        userId,
        friendUserId,
        sharedAt: new Date(Math.max(userJoinedAt, friendJoinedAt)).toISOString(),
      });
    }
  }
  return pairs;
}

export function friendInvitePath(inviteId: string): string {
  return `/friend-invites/${inviteId}`;
}

export function friendsMatchingSearch<
  T extends {
    name: string | null;
    username: string | null;
    email?: string | null;
  },
>(friends: readonly T[], query: string): T[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) {
    return [...friends];
  }
  return friends.filter((friend) =>
    `${friend.name ?? ""} ${friend.username ?? ""} ${friend.email ?? ""}`
      .toLocaleLowerCase()
      .includes(needle),
  );
}

function trimmedIdentity(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

/** Username and email shown under a friend so matching names stay distinct. */
export function friendListIdentity(friend: {
  name?: string | null;
  username?: string | null;
  email?: string | null;
}): string | null {
  const name = trimmedIdentity(friend.name);
  const username = trimmedIdentity(friend.username);
  const email = trimmedIdentity(friend.email);
  const title = (name || username || email).toLocaleLowerCase();
  const parts: string[] = [];

  if (username && name) {
    parts.push(`@${username}`);
  }
  if (email && email.toLocaleLowerCase() !== title) {
    parts.push(email);
  }

  return parts.length > 0 ? parts.join(" · ") : null;
}

export function partyInvitePlans<
  T extends {
    name: string;
    awardShowName: string;
    participantUserIds: readonly string[];
    pendingInviteeIds: readonly string[];
  },
>(
  parties: readonly T[],
  friendUserIds: readonly string[],
  query: string,
): Array<{ party: T; friendUserIds: string[] }> {
  const needle = query.trim().toLocaleLowerCase();
  const plans: Array<{ party: T; friendUserIds: string[] }> = [];
  for (const party of parties) {
    if (
      needle &&
      !`${party.name} ${party.awardShowName}`.toLocaleLowerCase().includes(needle)
    ) {
      continue;
    }
    const eligible = friendUserIds.filter(
      (friendUserId) =>
        !party.participantUserIds.includes(friendUserId) &&
        !party.pendingInviteeIds.includes(friendUserId),
    );
    if (eligible.length === 0) {
      continue;
    }
    plans.push({ party, friendUserIds: eligible });
  }
  return plans;
}

export type FriendInviteBlock =
  | "ok"
  | "self"
  | "notOwner"
  | "notFriend"
  | "alreadyMember"
  | "alreadyInvited";

export type FriendDirectoryRow = {
  userId: string;
  clerkId: string;
  name: string | null;
  username: string | null;
  email: string;
  imageUrl: string | null;
  partyId: string;
  partyName: string;
  awardShowName: string;
  awardShowYear: number;
  viewerJoinedAt: Date | string;
  friendJoinedAt: Date | string;
  removedAt: Date | string | null;
};

function timeOf(value: Date | string | null | undefined): number | null {
  if (value == null) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  return Number.isFinite(time) ? time : null;
}

function toIso(time: number): string {
  return new Date(time).toISOString();
}

export function isActiveFriend(input: {
  clerkId: string;
  sharedMoments: ReadonlyArray<number | null>;
  removedAt: Date | string | null;
}): boolean {
  if (isPlaceholderUser(input.clerkId)) {
    return false;
  }

  const moments = input.sharedMoments.filter(
    (moment): moment is number =>
      typeof moment === "number" && Number.isFinite(moment),
  );
  if (moments.length === 0) {
    return false;
  }

  if (input.removedAt == null) {
    return true;
  }

  const removedAt = timeOf(input.removedAt);
  if (removedAt === null) {
    return true;
  }

  return moments.some((moment) => moment > removedAt);
}

type GroupedFriend = {
  userId: string;
  clerkId: string;
  name: string | null;
  username: string | null;
  email: string;
  imageUrl: string | null;
  removedAt: number | null;
  parties: Map<string, FriendSharedParty & { sharedAtMs: number }>;
};

function sharedMomentMs(row: FriendDirectoryRow): number | null {
  const viewerJoinedAt = timeOf(row.viewerJoinedAt);
  const friendJoinedAt = timeOf(row.friendJoinedAt);
  if (viewerJoinedAt === null || friendJoinedAt === null) {
    return null;
  }
  return Math.max(viewerJoinedAt, friendJoinedAt);
}

export function buildFriendDirectory(rows: readonly FriendDirectoryRow[]): {
  friends: FriendSummary[];
  removed: RemovedFriendSummary[];
} {
  const grouped = new Map<string, GroupedFriend>();

  for (const row of rows) {
    if (!row.userId || isPlaceholderUser(row.clerkId)) {
      continue;
    }

    const moment = sharedMomentMs(row);
    if (moment === null) {
      continue;
    }

    let group = grouped.get(row.userId);
    if (!group) {
      group = {
        userId: row.userId,
        clerkId: row.clerkId,
        name: row.name,
        username: row.username,
        email: row.email,
        imageUrl: row.imageUrl,
        removedAt: timeOf(row.removedAt),
        parties: new Map(),
      };
      grouped.set(row.userId, group);
    }

    const removedAt = timeOf(row.removedAt);
    if (removedAt !== null) {
      group.removedAt = removedAt;
    }

    const existing = group.parties.get(row.partyId);
    if (!existing || moment > existing.sharedAtMs) {
      group.parties.set(row.partyId, {
        id: row.partyId,
        name: row.partyName,
        awardShowName: row.awardShowName,
        awardShowYear: row.awardShowYear,
        sharedAt: toIso(moment),
        sharedAtMs: moment,
      });
    }
  }

  const friends: FriendSummary[] = [];
  const removed: RemovedFriendSummary[] = [];

  for (const group of grouped.values()) {
    const sharedParties = [...group.parties.values()]
      .sort((a, b) => b.sharedAtMs - a.sharedAtMs)
      .map(({ sharedAtMs: _sharedAtMs, ...party }) => party);
    const lastShared = sharedParties[0];
    if (!lastShared) {
      continue;
    }

    const summary: FriendSummary = {
      userId: group.userId,
      name: group.name,
      username: group.username,
      email: group.email,
      imageUrl: group.imageUrl,
      sharedPartyCount: sharedParties.length,
      sharedParties,
      lastSharedAt: lastShared.sharedAt,
    };

    const active = isActiveFriend({
      clerkId: group.clerkId,
      sharedMoments: [...group.parties.values()].map(
        (party) => party.sharedAtMs,
      ),
      removedAt:
        group.removedAt === null ? null : new Date(group.removedAt).toISOString(),
    });

    if (active) {
      friends.push(summary);
      continue;
    }

    if (group.removedAt !== null) {
      removed.push({
        ...summary,
        removedAt: toIso(group.removedAt),
      });
    }
  }

  friends.sort((a, b) => {
    const byTime = Date.parse(b.lastSharedAt) - Date.parse(a.lastSharedAt);
    return byTime === 0 ? a.userId.localeCompare(b.userId) : byTime;
  });
  removed.sort((a, b) => {
    const byTime = Date.parse(b.removedAt) - Date.parse(a.removedAt);
    return byTime === 0 ? a.userId.localeCompare(b.userId) : byTime;
  });

  return { friends, removed };
}

export function canReserveFriendInvite(input: {
  memberCap: number | null;
  participantCount: number;
  pendingFriendInviteCount: number;
}): boolean {
  const { memberCap, participantCount, pendingFriendInviteCount } = input;
  if (!Number.isFinite(participantCount) || participantCount < 0) {
    return false;
  }
  if (!Number.isFinite(pendingFriendInviteCount) || pendingFriendInviteCount < 0) {
    return false;
  }
  if (memberCap === null) {
    return true;
  }
  if (!Number.isFinite(memberCap) || memberCap < 0) {
    return false;
  }
  return participantCount + pendingFriendInviteCount < memberCap;
}

export function friendInviteBlock(input: {
  actorUserId: string;
  friendUserId: string;
  partyOwnerId: string;
  isActiveFriend: boolean;
  isAlreadyMember: boolean;
  hasPendingInvite: boolean;
}): FriendInviteBlock {
  if (input.actorUserId === input.friendUserId) {
    return "self";
  }
  if (input.actorUserId !== input.partyOwnerId) {
    return "notOwner";
  }
  if (input.isAlreadyMember) {
    return "alreadyMember";
  }
  if (input.hasPendingInvite) {
    return "alreadyInvited";
  }
  if (!input.isActiveFriend) {
    return "notFriend";
  }
  return "ok";
}
